import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Grid,
  Box,
  Button,
  IconButton,
  Stack,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import OfferCard from "./offerCard";
import { fetchWalletOffers } from "./offersFirestore";
import type { Offer } from "../../lib/types/detailsTypes";
import { UserContext } from "../../contexts/user-context";
import WalletOfferDialog from "./walletOfferDialog";
import emptyChest from "../../assets/emptyChestGray.png";

const MyOffersPage: React.FC = () => {
  const context = useContext(UserContext);
  const navigate = useNavigate();
  const user = context?.user;
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);

  // Function to load wallet offers for the current user
  const loadOffers = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const fetched = await fetchWalletOffers(user.uid);
      setOffers(fetched);
    } catch (err) {
      console.error("Error fetching wallet offers:", err);
      setError("Failed to load offers. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const handleOfferDeleted = (offerId: string) => {
    setOffers((prevOffers) => prevOffers.filter((o) => o.id !== offerId));
  };

  // Style adjustments for a dark background
  const containerStyles = {
    py: 4,
    backgroundColor: "grey.900",
    minHeight: "100vh",
    color: "common.white",
  };

  if (loading) {
    return (
      <Container sx={{ ...containerStyles, textAlign: "center" }}>
        <CircularProgress color="inherit" />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container sx={containerStyles}>
        <Typography variant="h6" color="error">
          Please log in to view your offers.
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        ...containerStyles,
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Typography variant="h4" sx={{ mb: 2, fontWeight: "bold" }}>
        My Offers
      </Typography>

      {error && (
        <Typography variant="body1" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {offers.length === 0 ? (
        <Box
          sx={{
            mt: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 2,
          }}
        >
          <img
            src={emptyChest}
            alt="Empty Wallet"
            style={{ width: "150px", marginBottom: "16px" }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You don't seem to have any offers in your wallet yet.
          </Typography>

          <Stack direction="column" spacing={2}>
            <IconButton
              color="primary"
              size="medium"
              onClick={loadOffers}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                "&:hover": {
                  bgcolor: "action.hover",
                },
                aspectRatio: "1",
                width: 40,
              }}
              aria-label="Refresh offers"
            >
              <Refresh />
            </IconButton>
          </Stack>
        </Box>
      ) : (
        <Grid container spacing={0}>
          {offers.map((offer) => (
            <Grid item xs={12} sm={6} md={4} key={offer.id}>
              <Box
                sx={{ cursor: "pointer" }}
                onClick={() => setSelectedOffer(offer)}
              >
                <OfferCard offer={offer} />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Find more offers button */}
      <Box
        sx={{
          position: "fixed",
          bottom: "20%",
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
        }}
      >
        {/* <Stack direction="column" spacing={2}>
          {offers.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, textAlign: "center" }}
            >
              Let’s fix that!
            </Typography>
          )}
          <Button
            variant="contained"
            size="medium"
            onClick={() => navigate("/nearme")}
          >
            {offers.length === 0 ? "Find Offers Nearby" : "Discover More"}
          </Button>
        </Stack> */}
      </Box>

      {selectedOffer && (
        <WalletOfferDialog
          open={Boolean(selectedOffer)}
          offer={selectedOffer}
          onClose={() => setSelectedOffer(null)}
          userId={user.uid}
          onOfferDeleted={handleOfferDeleted}
        />
      )}
    </Container>
  );
};

export default MyOffersPage;
